import React, { useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import Card from 'components/inputs/Card';
import { Formik, Form } from 'formik';
import { Row, Col } from 'react-bootstrap';
import Button from 'components/inputs/Button';
import TextInput from 'components/inputs/Input';
import { useDispatch, useSelector } from 'react-redux';
import { UPDATE_CPO } from 'actions/partnerManagement';
import { useTranslation } from 'react-i18next';
import Select from 'components/inputs/Select';
import * as Yup from 'yup';
import './Configuration.scss';
import { useParams } from 'react-router';
import ToggleSwitch from 'components/inputs/ToggleSwitch';
import DatePickerInput from 'components/inputs/DatePickerInput';
import moment from 'moment';
import { NavContext } from 'components/privateRoute/PrivateRoute';

const ocpiOptions = [
  { value: '2.2', label: '2.2' },
  { value: '2.2.1', label: '2.2.1' },
];
const chargingTypeOptions = [
  { value: 'offline', label: 'Offline' },
  { value: 'online', label: 'Online' },
  { value: 'both', label: 'Both' },
];
const calculationOptions = [
  { value: 'fixed_amount', label: 'Fixed Amount' },
  { value: 'percentage', label: 'Percentage' },
];

// App Configuration Checklist items
const appChecklistItems = [
  {
    key: 'is_session_push_supported',
    label: 'Session Push Supported',
    default: true,
    description: '(We, as an EMSP, push real-time SoC and kWh updates to the CZ App.)',
  },
];

//Core Functionality Checklist items
const coreChecklistItems = [
  {
    key: 'is_reserve_now_supported',
    label: 'Reserve Now Supported',
    default: false,
    description: '(If the CPO supports Reserve Now, we as an EMSP send a reservation request before starting a session.)',
  },
  {
    key: 'allow_credential_post_as_response',
    label: 'Allow Credential Post as Response',
    default: true,
    description: '(Allowing credential post as a response.)',
  },
  {
    key: 'is_limit_location_enable',
    label: 'Limit Location Enable',
    default: false,
    description: '(Restricting specific stations and chargers to certain clients)',
  },
  {
    key: 'is_connector_patch_supported',
    label: 'Connector Patch Supported',
    default: false,
    description: '(Sends connector-level status with an extra / for the connector ID.)',
  },
  {
    key: 'is_custom_business_details_supported',
    label: 'Custom Business Details Supported',
    default: false,
    description: '(Allows sharing customer helpline and business details during handshake or credential posting.)',
  },
  { key: 'is_fleet_details_supported', label: 'Fleet Details Supported', default: false, description: '(Expect fleet details from EMSP.)' },
];

//Finance Checklist items
const financeChecklistItems = [
  {
    key: 'is_ocpi_customer_supported',
    label: 'OCPI Customer Supported',
    default: false,
    description: '(EMSP shares user details with us via a custom endpoint.)',
  },
];

// Validation Schema
const validationSchema = Yup.object().shape({
  session_timeout_in_seconds: Yup.number().min(1, 'Must be greater than zero').required('Required'),
  ocpi_standard: Yup.string().required('Required'),
  charging_type_supported: Yup.string().required('Required'),
  calculation_type: Yup.string().required('Required'),
  value: Yup.number().when('calculation_type', {
    is: 'percentage',
    then: Yup.number().min(0, 'Must be at least 0').max(100, 'Cannot exceed 100').required('Required'),
    otherwise: Yup.number().required('Required'),
  }),
  // CDB-1597 — only required once settlement is switched on, so a partner that
  // does not use it can still save the rest of this form.
  settlement_frequency_days: Yup.number().when('settlement_enabled', {
    is: true,
    then: Yup.number()
      .typeError('Enter a whole number of days')
      .integer('Enter a whole number of days')
      .min(0, 'Must be between 0 and 30')
      .max(30, 'Must be between 0 and 30')
      .required('Required'),
  }),
  settlement_start_date: Yup.mixed().when('settlement_enabled', {
    is: true,
    // Required rather than defaulted: this is the boundary against the finance
    // team's manual settlement, and guessing it either double-pays a day or
    // skips one entirely.
    then: Yup.mixed().required('Required'),
  }),
  settlement_max_cap: Yup.number().when('settlement_enabled', {
    is: true,
    then: Yup.number().typeError('Enter a valid amount').min(0, 'Cannot be negative').required('Required'),
  }),
  settlement_min_amount: Yup.number().when('settlement_enabled', {
    is: true,
    then: Yup.number().typeError('Enter a valid amount').min(0, 'Cannot be negative'),
  }),
  // Bank details are only mandatory once settlement is switched on: that is the
  // point at which we need somewhere to actually send the money.
  bankName: Yup.string().when('settlement_enabled', { is: true, then: Yup.string().required('Required') }),
  branch: Yup.string().when('settlement_enabled', { is: true, then: Yup.string().required('Required') }),
  account_no: Yup.string().when('settlement_enabled', {
    is: true,
    then: Yup.string()
      .trim()
      .matches(/^[0-9]{6,20}$/, 'Enter a valid account number')
      .required('Required'),
  }),
  ifscCode: Yup.string().when('settlement_enabled', {
    is: true,
    // Standard IFSC shape: 4 letters, 0, then 6 alphanumerics. Checked here as
    // well as server-side because a wrong IFSC fails only at payout time.
    then: Yup.string()
      .trim()
      .uppercase()
      .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Enter a valid IFSC code')
      .required('Required'),
  }),
  owner: Yup.string().when('settlement_enabled', { is: true, then: Yup.string().required('Required') }),
});

const Configuration = () => {
  const dispatch = useDispatch();
  const { navigateTo } = useContext(NavContext);
  const { ocpiId } = useParams();
  const { t } = useTranslation();
  let ocpiData = useSelector((state) => state.partnerManagement.partnerInfo) || {};
  if (ocpiData) {
    ocpiData = { ...ocpiData, calculation_type: ocpiData?.calculation?.calculation_type, value: ocpiData?.calculation?.value };
  }

  const initialValues = ocpiId
    ? {
        is_session_push_supported: ocpiData?.is_session_push_supported ?? true,
        session_timeout_in_seconds: ocpiData?.session_timeout_in_seconds ?? 900,
        is_reserve_now_supported: ocpiData?.is_reserve_now_supported ?? false,
        is_ocpi_customer_supported: ocpiData?.is_ocpi_customer_supported ?? false,
        allow_credential_post_as_response: ocpiData?.allow_credential_post_as_response ?? true,
        is_fleet_details_supported: ocpiData?.is_fleet_details_supported ?? false,
        is_limit_location_enable: ocpiData?.is_limit_location_enable ?? false,
        is_connector_patch_supported: ocpiData?.is_connector_patch_supported ?? false,
        is_custom_business_details_supported: ocpiData?.is_custom_business_details_supported ?? false,
        ocpi_standard: ocpiData?.ocpi_standard ?? '2.2.1',
        charging_type_supported: ocpiData?.charging_type_supported?.length ? ocpiData.charging_type_supported[0] : 'online',
        calculation_type: ocpiData?.calculation_type ?? 'fixed_amount',
        value: ocpiData?.value ?? 0,
        settlement_enabled: ocpiData?.cpo_settlement_config?.enabled ?? false,
        settlement_frequency_days: ocpiData?.cpo_settlement_config?.settlement_frequency_days ?? 1,
        settlement_start_date: ocpiData?.cpo_settlement_config?.settlement_start_date
          ? moment(ocpiData.cpo_settlement_config.settlement_start_date).toDate()
          : '',
        settlement_max_cap: ocpiData?.cpo_settlement_config?.max_cap ?? 10000,
        settlement_min_amount: ocpiData?.cpo_settlement_config?.min_settlement_amount ?? 0,
        // An absent run_mode means manual payment only. Never inferred as
        // automatic, because that is the state in which money leaves unattended.
        settlement_live: (ocpiData?.cpo_settlement_config?.run_mode ?? 'compute_only') === 'live',
        bankName: ocpiData?.bankName ?? '',
        branch: ocpiData?.branch ?? '',
        account_no: ocpiData?.account_no ?? '',
        ifscCode: ocpiData?.ifscCode ?? '',
        owner: ocpiData?.owner ?? '',
      }
    : {
        ...appChecklistItems.reduce((acc, item) => ({ ...acc, [item.key]: item.default }), {}),
        ...coreChecklistItems.reduce((acc, item) => ({ ...acc, [item.key]: item.default }), {}),
        ...financeChecklistItems.reduce((acc, item) => ({ ...acc, [item.key]: item.default }), {}),
        session_timeout_in_seconds: 900,
        ocpi_standard: '2.2.1',
        charging_type_supported: 'online',
        calculation_type: 'fixed_amount',
        value: 0,
        settlement_enabled: false,
        settlement_frequency_days: 1,
        settlement_start_date: '',
        settlement_max_cap: 10000,
        settlement_min_amount: 0,
        settlement_live: false,
        bankName: '',
        branch: '',
        account_no: '',
        ifscCode: '',
        owner: '',
      };

  return (
    <LayoutContainer>
      <Card className="configuration-card">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(data) => {
            const payload = { ...data, calculation: { calculation_type: data.calculation_type, value: Number(data.value) }, id: ocpiData?.id };
            delete payload.value;
            delete payload.calculation_type;

            // CDB-1597 — same flat-to-nested treatment the calculation block gets
            // above, so the request shape stays visible where it is built.
            payload.cpo_settlement_config = {
              enabled: !!data.settlement_enabled,
              run_mode: data.settlement_live ? 'live' : 'compute_only',
              settlement_frequency_days: Number(data.settlement_frequency_days),
              // null rather than an empty string when unset, so the server's
              // "required when enabled" rule sees an absent value instead of
              // rejecting a malformed date.
              settlement_start_date: data.settlement_start_date ? moment(data.settlement_start_date).toISOString() : null,
              max_cap: Number(data.settlement_max_cap),
              min_settlement_amount: Number(data.settlement_min_amount),
            };
            delete payload.settlement_enabled;
            delete payload.settlement_live;
            delete payload.settlement_frequency_days;
            delete payload.settlement_start_date;
            delete payload.settlement_max_cap;
            delete payload.settlement_min_amount;
            dispatch({ type: UPDATE_CPO.REQUEST, payload, cb: () => navigateTo('/OCPI-Partner') });
          }}
        >
          {({ handleSubmit, handleChange, values, errors, setFieldTouched, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <div className="checklist-container">
                <section>
                  <span className="section_title">Core Functionality</span>
                  <Row>
                    {coreChecklistItems.map((item) => (
                      <Col lg={6} md={6} sm={12} key={item.key} className="checklist-item">
                        <div className="form-group">
                          <div className="switch-container">
                            <label htmlFor={item.key} className="checklist-label">
                              {item.label}
                            </label>
                            <ToggleSwitch
                              name={item.key}
                              checked={values[item.key] || false}
                              onChange={(e) => setFieldValue(item.key, e.target.checked)}
                              className="custom-switch"
                            />
                          </div>
                          <small className="field-description">{item.description}</small>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <Row>
                    <Col>
                      <label htmlFor="value" className="checklist-label">
                        {'Session Timeout (Seconds)'}
                        <br />
                        <br />
                        <small className="field-description">Enter the session timeout in seconds.</small>
                      </label>
                      <TextInput
                        isRequired={true}
                        name="session_timeout_in_seconds"
                        type="number"
                        placeholder="Enter session timeout (in seconds)"
                        value={values?.session_timeout_in_seconds}
                        onChange={handleChange}
                        onTouchStart={() => {
                          setFieldTouched('session_timeout_in_seconds', true);
                        }}
                        error={errors.session_timeout_in_seconds ? 'Session timeout is required!' : null}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <label htmlFor="value" className="checklist-label">
                        {'OCPI Standard'}
                        <br />
                        <br />
                        <small className="field-description">(Select OCPI version: 2.2 or 2.2.1.)</small>
                      </label>

                      <Select
                        isRequired={ocpiData ? false : true}
                        options={ocpiOptions}
                        placeholder={'ocpi standard'}
                        name="ocpi_standard"
                        value={values?.ocpi_standard}
                        onMenuScrollDown={true}
                        onChange={(val) => {
                          setFieldValue(`ocpi_standard`, val);
                        }}
                        error={errors.ocpi_standard ? 'OCPI version is required!' : null}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <label htmlFor="value" className="checklist-label">
                        {'Charging Type Supported'}
                        <br />
                        <br />
                        <small className="field-description">(Select charging type: offline, both, or online.)</small>
                      </label>
                      <Select
                        isRequired={ocpiData ? false : true}
                        options={chargingTypeOptions}
                        placeholder={'charging type supported'}
                        name="charging_type_supported"
                        value={values?.charging_type_supported}
                        onMenuScrollDown={true}
                        onChange={(val) => {
                          setFieldValue(`charging_type_supported`, val);
                        }}
                        error={errors.charging_type_supported ? 'Charging type is required!' : null}
                      />
                    </Col>
                  </Row>
                </section>
                <br />
                <section>
                  <span className="section_title">Finance </span>
                  <Row>
                    {financeChecklistItems.map((item) => (
                      <Col lg={6} md={6} sm={12} key={item.key} className="checklist-item">
                        <div className="form-group">
                          <div className="switch-container">
                            <label htmlFor={item.key} className="checklist-label">
                              {item.label}
                            </label>
                            <ToggleSwitch
                              name={item.key}
                              checked={values[item.key] || false}
                              onChange={(e) => setFieldValue(item.key, e.target.checked)}
                              className="custom-switch"
                            />
                          </div>
                          <small className="field-description">{item.description}</small>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <Row>
                    <Col>
                      <label htmlFor="value" className="checklist-label">
                        {'Discount Type'}
                        <br />
                        <br />
                        <small className="field-description">(Define calculation method: percentage or fixed amount.)</small>
                      </label>
                      <Select
                        isRequired={ocpiData ? false : true}
                        options={calculationOptions}
                        placeholder={'calculation_type'}
                        name="Calculation Type"
                        value={values?.calculation_type}
                        onMenuScrollDown={true}
                        onChange={(val) => {
                          setFieldValue(`calculation_type`, val);
                        }}
                        error={errors.calculation_type ? 'Select Calculation type!' : null}
                      />
                    </Col>
                  </Row>

                  <Row>
                    {/* Amount Input Field */}
                    <Col lg={6} md={6} sm={12}>
                      <div className="form-group">
                        <label htmlFor="value" className="checklist-label">
                          {values?.calculation_type === 'percentage' ? 'Amount (Percentage)' : 'Amount (Fixed)'}
                        </label>
                        <small className="field-description">
                          {values?.calculation_type === 'percentage' ? 'Enter a value between 0 - 100%.' : 'Enter a fixed amount (no limit).'}
                        </small>
                        <TextInput
                          isRequired={true}
                          name="value"
                          type="number"
                          placeholder={values?.calculation_type === 'percentage' ? 'Enter percentage (0-100%)' : 'Enter fixed amount'}
                          value={values?.value}
                          onChange={handleChange}
                          onTouchStart={() => {
                            setFieldTouched('value', true);
                          }}
                          error={errors.value ? 'Amount is required!' : null}
                        />
                      </div>
                    </Col>
                  </Row>
                </section>
                <br />
                <section>
                  <span className="section_title">App Configuration</span>
                  <Row>
                    {appChecklistItems.map((item) => (
                      <Col lg={6} md={6} sm={12} key={item.key} className="checklist-item">
                        <div className="form-group">
                          <div className="switch-container">
                            <label htmlFor={item.key} className="checklist-label">
                              {item.label}
                            </label>
                            <ToggleSwitch
                              name={item.key}
                              checked={values[item.key] || false}
                              onChange={(e) => setFieldValue(item.key, e.target.checked)}
                              className="custom-switch"
                            />
                          </div>
                          <small className="field-description">{item.description}</small>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </section>
                <br />
                <section>
                  <span className="section_title">T+X Settlement (Outbound to CPO Partner)</span>
                  <Row>
                    <Col lg={6} md={6} sm={12} className="checklist-item">
                      <div className="form-group">
                        <div className="switch-container">
                          <label htmlFor="settlement_enabled" className="checklist-label">
                            Enable Automatic Settlement
                          </label>
                          <ToggleSwitch
                            name="settlement_enabled"
                            checked={values.settlement_enabled || false}
                            onChange={(e) => setFieldValue('settlement_enabled', e.target.checked)}
                            className="custom-switch"
                          />
                        </div>
                        <small className="field-description">(Settle collected amounts to this CPO partner automatically on a T+X cycle.)</small>
                      </div>
                    </Col>
                  </Row>

                  {/* The rest only applies once settlement is on. Hidden otherwise
                      so the form never implies a disabled partner is configured. */}
                  {values.settlement_enabled && (
                    <React.Fragment>
                      {/* Where the money goes. Required from the moment settlement
                          is enabled, because an incomplete account is only
                          discovered at payout time otherwise. */}
                      <Row>
                        <Col lg={12} md={12} sm={12}>
                          <span className="section_title">Partner Bank Details</span>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={6} md={6} sm={12}>
                          <div className="form-group">
                            <label htmlFor="owner" className="checklist-label">
                              Account Holder Name
                            </label>
                            <TextInput
                              isRequired
                              name="owner"
                              placeholder="Name exactly as on the bank account"
                              value={values.owner}
                              onChange={handleChange}
                              onTouchStart={() => setFieldTouched('owner', true)}
                              error={errors.owner ? errors.owner : null}
                            />
                          </div>
                        </Col>
                        <Col lg={6} md={6} sm={12}>
                          <div className="form-group">
                            <label htmlFor="account_no" className="checklist-label">
                              Account Number
                            </label>
                            <TextInput
                              isRequired
                              name="account_no"
                              placeholder="Enter account number"
                              value={values.account_no}
                              onChange={handleChange}
                              onTouchStart={() => setFieldTouched('account_no', true)}
                              error={errors.account_no ? errors.account_no : null}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={6} md={6} sm={12}>
                          <div className="form-group">
                            <label htmlFor="ifscCode" className="checklist-label">
                              IFSC Code
                            </label>
                            <TextInput
                              isRequired
                              name="ifscCode"
                              placeholder="e.g. HDFC0001234"
                              value={values.ifscCode}
                              // Upper-cased as typed: IFSC codes are always upper
                              // case, and the validation pattern expects it.
                              onChange={(e) => setFieldValue('ifscCode', (e.target.value || '').toUpperCase())}
                              onTouchStart={() => setFieldTouched('ifscCode', true)}
                              error={errors.ifscCode ? errors.ifscCode : null}
                            />
                          </div>
                        </Col>
                        <Col lg={6} md={6} sm={12}>
                          <div className="form-group">
                            <label htmlFor="bankName" className="checklist-label">
                              Bank Name
                            </label>
                            <TextInput
                              isRequired
                              name="bankName"
                              placeholder="Enter bank name"
                              value={values.bankName}
                              onChange={handleChange}
                              onTouchStart={() => setFieldTouched('bankName', true)}
                              error={errors.bankName ? errors.bankName : null}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={6} md={6} sm={12}>
                          <div className="form-group">
                            <label htmlFor="branch" className="checklist-label">
                              Branch
                            </label>
                            <TextInput
                              isRequired
                              name="branch"
                              placeholder="Enter branch"
                              value={values.branch}
                              onChange={handleChange}
                              onTouchStart={() => setFieldTouched('branch', true)}
                              error={errors.branch ? errors.branch : null}
                            />
                          </div>
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}

                  {values.settlement_enabled && (
                    <React.Fragment>
                      <Row>
                        <Col lg={6} md={6} sm={12}>
                          <div className="form-group">
                            <label htmlFor="settlement_frequency_days" className="checklist-label">
                              Settlement Frequency (T+X)
                            </label>
                            <small className="field-description">(Days after the session day. 1 = settle yesterday&apos;s sessions today.)</small>
                            <TextInput
                              isRequired={true}
                              name="settlement_frequency_days"
                              type="number"
                              placeholder="Enter number of days (0-30)"
                              value={values.settlement_frequency_days}
                              onChange={handleChange}
                              onTouchStart={() => setFieldTouched('settlement_frequency_days', true)}
                              error={errors.settlement_frequency_days ? errors.settlement_frequency_days : null}
                            />
                          </div>
                        </Col>
                        <Col lg={6} md={6} sm={12}>
                          <div className="form-group">
                            <label htmlFor="settlement_start_date" className="checklist-label">
                              Settlement Start Date
                            </label>
                            <small className="field-description">
                              (Sessions before this date remain with the finance team&apos;s manual settlement and are never picked up here.)
                            </small>
                            <DatePickerInput
                              isRequired
                              name="settlement_start_date"
                              value={values.settlement_start_date}
                              onChange={(dateString, jsDate) => setFieldValue('settlement_start_date', jsDate || dateString)}
                              error={errors.settlement_start_date ? errors.settlement_start_date : null}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={6} md={6} sm={12}>
                          <div className="form-group">
                            <label htmlFor="settlement_max_cap" className="checklist-label">
                              Approval Required Above (INR)
                            </label>
                            <small className="field-description">
                              (Amounts above this need a person to release them. At or below, payment is automatic.)
                            </small>
                            <TextInput
                              name="settlement_max_cap"
                              type="number"
                              placeholder="Enter amount"
                              value={values.settlement_max_cap}
                              onChange={handleChange}
                              onTouchStart={() => setFieldTouched('settlement_max_cap', true)}
                              error={errors.settlement_max_cap ? errors.settlement_max_cap : null}
                            />
                          </div>
                        </Col>
                        <Col lg={6} md={6} sm={12}>
                          <div className="form-group">
                            <label htmlFor="settlement_min_amount" className="checklist-label">
                              Minimum Settlement Amount (INR)
                            </label>
                            <small className="field-description">(A total below this is held and rolls into the next cycle.)</small>
                            <TextInput
                              name="settlement_min_amount"
                              type="number"
                              placeholder="Enter amount"
                              value={values.settlement_min_amount}
                              onChange={handleChange}
                              onTouchStart={() => setFieldTouched('settlement_min_amount', true)}
                              error={errors.settlement_min_amount ? errors.settlement_min_amount : null}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={6} md={6} sm={12} className="checklist-item">
                          <div className="form-group">
                            <div className="switch-container">
                              <label htmlFor="settlement_live" className="checklist-label">
                                Automatic Payments
                              </label>
                              <ToggleSwitch
                                name="settlement_live"
                                checked={values.settlement_live || false}
                                onChange={(e) => setFieldValue('settlement_live', e.target.checked)}
                                className="custom-switch"
                              />
                            </div>
                            {/* Stated on the form rather than left to documentation,
                                because this switch is what allows money to move. */}
                            <small className="field-description">
                              {values.settlement_live
                                ? `(ON: the daily run pays any settlement of ₹${
                                    Number(values.settlement_max_cap) || 0
                                  } or less automatically. Above that, it waits for approval.)`
                                : '(OFF: nothing is paid automatically. Settlements are still created and can be paid by hand from the settlement list.)'}
                            </small>
                          </div>
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}
                </section>
              </div>
              <div className="cpo_save_btn">
                <Button type="submit" className="cpo_save_btn__1">
                  {t('button.save')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Card>
    </LayoutContainer>
  );
};

export default Configuration;
